<template>
  <div
    v-if="isSetVisible && computedAssets.length"
    id="marketing-materials"
    :class="$style.marketingMaterialSet"
    data-marketing-material-set-v="3.1.0"
  >
    <div :class="$style.innerWrapper">
      <div :class="$style.titleBar" @click="isExpanded = !isExpanded">
        <h3 :class="$style.title">
          {{ localizedStrings['marketing-material'] || 'Marketing Material' }}
        </h3>
        <div :class="$style.toggle">
          <IconChevronUp v-if="isExpanded" />
          <IconChevronDown v-else />
        </div>
      </div>
      <div v-if="isExpanded" :class="$style.marketingAssets">
        <MarketingAsset
          v-for="asset in computedAssets"
          :key="asset?.sys?.id"
          :data="asset"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { TypeMarketingMaterialSetWithoutUnresolvableLinksResponse } from '~/types/contentful'

const { $visibility, $userAccessCheck } = useNuxtApp()
const { highestUserLevel } = storeToRefs(useUserStore())
const { localizedStrings } = useLocalizedStrings()

const isExpanded = ref(true)

const props = defineProps<{
  data: TypeMarketingMaterialSetWithoutUnresolvableLinksResponse
}>()

const isSetVisible = computed(() => {
  return (
    $visibility(props.data?.fields?.visibility) &&
    $userAccessCheck(props.data?.fields?.accessLevel, highestUserLevel.value)
  )
})

const computedAssets = computed(() => {
  return (
    props.data?.fields?.assets?.filter((asset) => {
      return (
        $visibility(asset?.fields?.visibility) &&
        $userAccessCheck(asset?.fields?.accessLevel, highestUserLevel.value)
      )
    }) || []
  )
})
</script>

<style lang="scss" module>
.marketingMaterialSet {
  background-color: var(--white);
  padding: 68px 20px;

  @include media('md') {
    padding: 75px 0;
  }
}

.innerWrapper {
  max-width: 1148px;
  margin: auto;
}

.titleBar {
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
}

.title {
  font-family: var(--roboto-condensed);
  font-size: to-rem(26px);
  font-weight: bold;
  line-height: math.div(32, 26);

  @include media('md') {
    font-size: to-rem(42px);
    line-height: math.div(46, 24);
  }
}

.toggle {
  align-items: center;
  background-color: var(--purple);
  border-radius: 50%;
  color: var(--white);
  display: flex;
  flex: 0 0 32px;
  height: 32px;
  justify-content: center;
  margin-left: 13px;
  width: 32px;

  svg {
    width: 11px;
  }
}

.marketingAssets {
  display: grid;
  grid-gap: 30px;
  grid-template-columns: 1fr;
  margin: 20px 22px 0;

  @include media('md') {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    max-width: 1150px;
    margin: 30px auto 0;
    padding-bottom: 60px;
  }
}
</style>
